import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { ComponentType } from '@angular/cdk/portal';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable, TemplateRef } from '@angular/core';
import { filter, take, pairwise, map } from 'rxjs/operators';
import { RightSideDialogData } from 'src/shared/components/dynamic-dialog-container/dynamic-dialog-container';
import { CustomNavController } from '../navigation/custom-nav-controller';
import { HeaderProvider } from 'src/providers/header/header';
import { WindowService } from 'src/shared/providers/window.service';

import { routes } from 'src/app/aggregators/pages';
import { Router } from '@angular/router';

@Injectable()
export class RightSidePanelService {

  historyContext$ = new BehaviorSubject<Array<ComponentType<unknown> | TemplateRef<any>>>([]);
  isOpen$ = new BehaviorSubject<boolean>(false);
  isClosingPanel: boolean;
  currentComponent$ = new BehaviorSubject<ComponentType<unknown> | TemplateRef<any>>(null);
  currentComponentData: any;
  dropDownMenuAction$ = new BehaviorSubject<string>(null);
  pageTitle$ = new BehaviorSubject<string>(null);
  pageSubtitle$ = new BehaviorSubject<string>(null);
  pageSubtitleIcon$ = new BehaviorSubject<string>(null);
  /** Hover copy for an info icon rendered right after the dialog title. */
  pageTitleTooltip$ = new BehaviorSubject<string>(null);
  isLoading$ = new BehaviorSubject<boolean>(false);
  requestMadeInsideRightSidePanel$ = new BehaviorSubject<boolean>(false);
  linkAction$ = new Subject<void>();
  sendMessageAction$ = new Subject<{ type: string, message: string, metadata?: any }>();
  linkData: any;

  // Header toggle properties
  headerToggleVisible$ = new BehaviorSubject<boolean>(false);
  headerToggleValue$ = new BehaviorSubject<boolean>(false);
  headerToggleChanged$ = new Subject<boolean>();
  headerSpinnerVisible$ = new BehaviorSubject<boolean>(false);

  // Header icons
  headerIcons$ = new BehaviorSubject<HeaderIcon[]>([]);
  headerIconClicked$ = new Subject<string>();

  // Compact mode (e.g., success page)
  isCompactMode$ = new BehaviorSubject<boolean>(false);

  // Header dropdown menu
  headerDropdownMenu$ = new BehaviorSubject<HeaderDropdownItem[]>([]);

  // Custom back action (allows components to override the dialog back button behavior)
  customBackAction$ = new BehaviorSubject<(() => void) | null>(null);

  // QA-only skip action: when a page sets this, holding the dialog's X button for 5s
  // reveals a "Skip" menu that invokes it (used to fast-forward gated steps in testing).
  customSkipAction$ = new BehaviorSubject<(() => void) | null>(null);

  // Header filter (e.g., address selector below title)
  headerFilterItems$ = new BehaviorSubject<{ value: any; viewValue: string }[]>([]);
  headerFilterValue$ = new BehaviorSubject<any>(null);
  headerFilterChanged$ = new Subject<any>();

  constructor(
    public headerProvider: HeaderProvider,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private router: Router,
    private windowService: WindowService
  ) {}

  /**
   * Save the component in the history context
   * @param component The component to save
   * @returns Promise<void>
   */
  async saveDialogHistoryContext(
    component: ComponentType<unknown> | TemplateRef<any>,
    data: any = null
  ): Promise<void> {
    this.linkData = null;
    this.historyContext$.next([...this.historyContext$.getValue(), component]);
    this.currentComponent$.next(component);
    this.currentComponentData = data;
  }

  /**
   * Replace the component in the dialog
   * @param component The component to replace
   * @param dialog The dialog reference
   * @returns void
   */
  async replaceComponentInDialog(component: ComponentType<unknown>): Promise<void> {
    await this.saveDialogHistoryContext(component);
  }

  /**
   * Set the dialog page title
   * @param title The title to set
   * @param dialog The dialog reference
   * @returns void
   */
  setDialogPageTitle(title: string, tooltip: string = null): void {
    this.pageTitle$.next(title);
    this.pageTitleTooltip$.next(tooltip);
  }

  setDialogPageSubtitle(subtitle: string, icon: string = null): void {
    this.pageSubtitle$.next(subtitle);
    this.pageSubtitleIcon$.next(icon);
  }

  /**
   * Set the request made inside right side panel
   * @param value The value to set
   * @returns void
   */
  setRequestMadeInsideRightSidePanel(value: boolean): void {
    const isOpen = this.isOpen$.getValue();
    if (!isOpen) {
      this.requestMadeInsideRightSidePanel$.next(false);
      return;
    }
    this.requestMadeInsideRightSidePanel$.next(value);
  }

  /**
   * Notifies when the right side panel is closed after being opened.
   * @returns Observable that emits once when the panel is closed.
   */
  panelClosed(): Observable<boolean> {
    return this.isOpen$.pipe(
      filter((isOpen) => !isOpen), // Emit only when isOpen becomes false
      take(1) // Complete after the first emission
    );
  }

  /**
   * Notifies when the right side panel is closed after being opened.
   * @returns Observable that emits when the panel is closed.
   */
  afterPanelClosed(): Observable<boolean> {
    return this.isOpen$.pipe(
      pairwise(),  // Emit the previous and current value as an array
      filter(([prev, curr]) => prev && !curr),  // Check if the previous value was true and current is false
      map(([_prev, curr]) => curr)  // Map to the current value
    );
  }

  async openRightSidePanelWithTemplate(template: TemplateRef<any>, data: RightSideDialogData = null) {
    this.historyContext$.next([]);
    this.isOpen$.next(true);
    this.currentComponent$.next(null);
    this.currentComponentData = data;
    await this.saveDialogHistoryContext(template, data);
  }

  /**
   * Open the right side panel
   * @param component The component to open
   * @returns void
   */
  async openRightSidePanel(component: ComponentType<unknown>, data: RightSideDialogData = null) {
    this.historyContext$.next([]);
    this.isOpen$.next(true);
    this.currentComponent$.next(null);
    this.currentComponentData = data;
    await this.saveDialogHistoryContext(component, data);
  }

  /**
   * Go back to the previous component
   */
  async goBack(): Promise<void> {
    const historyContext =
      this.historyContext$.getValue();
    historyContext.pop();
    const previousComponent = historyContext[historyContext.length - 1];
    this.historyContext$.next(historyContext);
    this.currentComponent$.next(previousComponent);
  }

  async goBackInLayers(layers = 1): Promise<void> {
    for (let i = 0; i < layers; i++) {
      await this.goBack();
    }
  }

  /**
   * Close the right side panel
   * @returns void
   */
  async closeRightSidePanel() {
    const currentRoute = this.router.url;
    if (currentRoute === '/to-dos') {
      return;
    }
    this.isClosingPanel = true;
    this.headerProvider.showConciergePage = false;
    this.headerProvider.showNotificationCenter = false;
    const dialog = document.getElementById('right-side-dialog');
    const backdrop = dialog?.parentElement;
    if (!dialog || !backdrop) {
      this.storage.delete('dialog-params');
      this.storage.delete('dialog-right-side-open');
      return;
    }
    backdrop.classList.add('modal-fade-out');
    dialog.classList.add('modal-scale-out');
    setTimeout(async () => {
      this.historyContext$.next([]);
      this.isOpen$.next(false);
      this.currentComponent$.next(null);
      this.pageTitle$.next(null);
      this.pageSubtitle$.next(null);
      this.pageSubtitleIcon$.next(null);
      this.headerFilterItems$.next([]);
      this.headerFilterValue$.next(null);
      this.isCompactMode$.next(false);
      this.customBackAction$.next(null);
      this.customSkipAction$.next(null);
      this.storage.delete('dialog-params');
      this.storage.delete('dialog-right-side-open');
      const _dialogParams = await this.storage.retrieve('dialog-params');
      this.isClosingPanel = false;
    }, 200);
  }

  /**
   * Set the dialog loading state
   * @param isLoading The loading state to set
   * @returns void
   */
  setDialogLoading(isLoading: boolean): void {
    this.isLoading$.next(isLoading);
  }

  /**
   * Navigate to a route with or without the right side panel
   * @param route
   * @param params
   * @param component
   * @param checkIsOpen
   * @returns
   */
  async navigateTo(route: string, params: any = {}, component: ComponentType<unknown> | null = null, checkIsOpen = true): Promise<void> {
    // Callers inside SharedModule omit the component (importing it would close a module
    // cycle) and rely on the route registry. Resolve it before the branch below: a panel
    // opened via openRightSidePanel() never wrote 'dialog-right-side-open', so that branch
    // used to re-open the dialog with a null component — a title over an empty body.
    component = component || this.resolveComponentFromRoute(route);
    const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
    if (this.windowService.isDesktopRes && !isRightSidePanelAlreadyOpen) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      const resolvedComponent = component || this.getComponentFromRoute(route);
      return this.openRightSidePanel(resolvedComponent);
    }
    try {
      component = component ? component : this.getComponentFromRoute(route);
      const dialogParams = await this.storage.retrieve('dialog-params');
      params = { ...dialogParams, ...params };
      await this.storage.save('dialog-params', params);
      const isRightSideContent = await this.storage.retrieve('dialog-right-side-open');
      const isOpen = this.isOpen$.getValue();
      if (isRightSideContent && (checkIsOpen ? isOpen : true)) {
        this.storage.save('dialog-right-side-open', true);
        const historyContext = this.historyContext$.getValue();
        if (historyContext.length === 0) {
          this.openRightSidePanel(component);
          return;
        }
        this.replaceComponentInDialog(component);
      } else {
        this.navCtrl.navigateForward(route, params);
      }
    } catch (_err) {
      // Silent error handling - navigation failure is logged elsewhere
    }
  }

  getComponentFromRoute(route) {
    const data = routes.find((item) => item.path === route);
    return data.component;
  }

  private resolveComponentFromRoute(route: string): ComponentType<unknown> | null {
    try {
      return this.getComponentFromRoute(route) || null;
    } catch {
      return null;
    }
  }

  emitDropDownMenuAction(action: string): void {
    this.dropDownMenuAction$.next(action);
    // Reset the action to prevent re-triggering on new subscriptions
    setTimeout(() => this.dropDownMenuAction$.next(null), 0);
  }

  setLinkActionData(icon: string): void {
    this.linkData = {
      icon
    };
  }

  emitLinkAction(): void {
    this.linkAction$.next();
  }

  emitSendMessageAction(type: string, message: string, metadata?: any): void {
    this.sendMessageAction$.next({ type, message, metadata });
  }

  /**
   * Show the header toggle with an initial value
   * @param initialValue The initial toggle value
   */
  showHeaderToggle(initialValue = false): void {
    this.headerToggleValue$.next(initialValue);
    this.headerToggleVisible$.next(true);
  }

  /**
   * Hide the header toggle
   */
  hideHeaderToggle(): void {
    this.headerToggleVisible$.next(false);
  }

  /**
   * Set the header toggle value
   * @param value The toggle value
   */
  setHeaderToggleValue(value: boolean): void {
    this.headerToggleValue$.next(value);
  }

  /**
   * Emit when the header toggle is changed by the user
   * @param value The new toggle value
   */
  emitHeaderToggleChanged(value: boolean): void {
    this.headerToggleValue$.next(value);
    this.headerToggleChanged$.next(value);
  }

  setHeaderIcons(icons: HeaderIcon[]): void {
    this.headerIcons$.next(icons);
  }

  clearHeaderIcons(): void {
    this.headerIcons$.next([]);
  }

  emitHeaderIconClicked(iconId: string): void {
    this.headerIconClicked$.next(iconId);
  }

  setHeaderDropdownMenu(items: HeaderDropdownItem[]): void {
    this.headerDropdownMenu$.next(items);
  }

  clearHeaderDropdownMenu(): void {
    this.headerDropdownMenu$.next([]);
  }

  setHeaderFilter(items: { value: any; viewValue: string }[], selectedValue: any): void {
    this.headerFilterItems$.next(items);
    this.headerFilterValue$.next(selectedValue);
  }

  clearHeaderFilter(): void {
    this.headerFilterItems$.next([]);
    this.headerFilterValue$.next(null);
  }

  emitHeaderFilterChanged(value: any): void {
    this.headerFilterValue$.next(value);
    this.headerFilterChanged$.next(value);
  }

}

export interface HeaderIcon {
  id: string;
  icon: string;
  tooltip?: string;
  label?: string;
}

export interface HeaderDropdownItem {
  label: string;
  action: string;
}
